import { IFundManager } from '@kurtosys/types/api/dictionary/IFundManager.js';
import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency.js';
import { ITransformClassDependency } from '@kurtosys/types/dependencies/ITransformClassDependency.js';
import { TConditionalHelperDependencies } from '@kurtosys/types/helpers/TConditionalHelperDependencies.js';
import { IExecutionOptions } from '@kurtosys/types/query/IExecutionOptions.js';
import { IQueryOptions } from '@kurtosys/types/query/IQueryOptions.js';
import { IQueryOptionsFundManagers } from '@kurtosys/types/query/options/IQueryOptionsFundManagers.js';

import { getValueFromStringNotation } from '../../common/commonUtils/object.js';
import { helpers } from '../../common/index.js';

import { BaseCollectionQuery } from './base/BaseCollectionQuery.js';

export class FundManagersQuery extends BaseCollectionQuery<IQueryOptionsFundManagers> {
	constructor(
		public dependencies: IQueryClassDependency & ITransformClassDependency,
		public queryOptions: IQueryOptions,
		public translationHelper?: helpers.TranslationHelper,
	) {
		super(dependencies, queryOptions, translationHelper);
	}

	initialize(executionOptions: IExecutionOptions): void {
		this.fillKeyFromQuery('code', 'codeQuery', executionOptions);
		this.fillKeyFromQuery('key', 'keyQuery', executionOptions);
		this.fillKeyFromQuery('extendedKey', 'extendedKeyQuery', executionOptions);
	}
	getOptions(): IQueryOptionsFundManagers {
		const {
			entityType,
			code,
			codeQuery,
			key,
			keyQuery,
			extendedKey,
			collectionItemConditional,
			fields,
			sort,
			index,
			startIndex,
			endIndex,
			entityKey,
			entityIndex,
			options = {},
		} = this.queryOptions;
		return {
			entityType,
			code,
			codeQuery,
			key,
			keyQuery,
			extendedKey,
			collectionItemConditional,
			fields,
			sort,
			index,
			startIndex,
			endIndex,
			entityKey,
			entityIndex,
			...(options.fundManagers || {}),
		};
	}
	getRawValue<T = any>(executionOptions: IExecutionOptions): T {
		const { sort, startIndex, index } = this.options;
		return this.processEntities(executionOptions, (entity) => {
			if (entity) {
				let fundManagers = this.getFundManagers(executionOptions) as any;
				if (sort) {
					fundManagers = this.applySort(fundManagers, sort, undefined, executionOptions.inputs);
				}
				if (fundManagers) {
					const workingFundManagers = fundManagers.map((fundManager): unknown =>
						this.getFundManagerResult(executionOptions, fundManager),
					);
					if (index !== undefined || startIndex !== undefined) {
						return this.getItem(executionOptions, workingFundManagers);
					}
					return workingFundManagers;
				}
			}
		});
	}
	getFundManagers(executionOptions: IExecutionOptions): IFundManager[] {
		const conditionalHelperDependencies: TConditionalHelperDependencies = {
			queryClass: this.dependencies.queryClass,
		};
		const conditionalHelper = new helpers.ConditionalHelper(
			conditionalHelperDependencies,
			{ conditions: [] },
			this.translationHelper,
		);
		const { code, collectionItemConditional = { conditions: [] } } = this.options;
		if (code && !collectionItemConditional.conditions.find((condition): boolean => condition.field === 'code')) {
			collectionItemConditional.conditions.push({
				field: 'code',
				operator: '=',
				value: code,
			});
		}
		const collectionItems = this.getSubCollection(executionOptions);
		if (collectionItems) {
			return collectionItems.filter((item) =>
				conditionalHelper.matches(item, collectionItemConditional, executionOptions),
			);
		}
	}
	getFundManagerResult<T>(executionOptions: IExecutionOptions, fundManager: IFundManager): T {
		const { fields } = this.options;
		if (!fields || fields.length === 0) {
			const { key, extendedKey } = this.options;
			return this.getFundManagerValue(executionOptions, fundManager, key, extendedKey) as any;
		}

		const fundManagerResult: any = {};
		for (const field of fields) {
			const { name, key, extendedKey } = field;
			fundManagerResult[name] = this.getFundManagerValue(executionOptions, fundManager, key, extendedKey);
		}
		return fundManagerResult;
	}
	getFundManagerValue<T>(
		executionOptions: IExecutionOptions,
		fundManager: IFundManager,
		key: string = null,
		extendedKey: string = null,
	): T {
		if (fundManager) {
			if (key) {
				const { biography: biographies, extended } = fundManager;
				if (key === 'biography' && biographies) {
					const { culture, fallbackCulture } = executionOptions;
					let biographyItem = biographies.find((biography): boolean => biography.culture === culture);
					if (!biographyItem) {
						biographyItem = biographies.find((biography): boolean => biography.culture === fallbackCulture);
					}
					return (biographyItem && biographyItem.biography) as any;
				}
				if (key === 'extended' && extended && extendedKey) {
					const extendedValue = getValueFromStringNotation(extendedKey, extended);
					if (extendedValue) {
						if (extendedValue.value) {
							return extendedValue.value;
						}
						return extendedValue as T;
					}
				}
				return fundManager[key];
			}
			return fundManager as any;
		}
		return;
	}
	getFundManager(executionOptions: IExecutionOptions): IFundManager {
		const fundManagers = this.getFundManagers(executionOptions);
		return this.getItem(executionOptions, fundManagers);
	}
}
